/**
 * ReferralAdminPanel — Admin referral program dashboard.
 *
 * Shows platform-wide referral stats: total redemptions, conversion rate,
 * top 10 referrers table, and a recent-activity feed.
 *
 * Placed in: Admin → Finance → Referrals tab
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Users, TrendingUp, Gift, Loader2, RefreshCw, Award,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { PanelHeader } from './_shared';
import { referralAdminApi } from '@/lib/api';

function StatCard({ icon: Icon, label, value, sub, color = 'text-primary' }) {
  return (
    <div className="rounded-md border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-1">
        <Icon className={`h-3.5 w-3.5 ${color}`} /> {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ReferralAdminPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await referralAdminApi.stats();
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const referrers = data?.top_referrers || [];
  const recent    = data?.recent_redemptions || [];

  // bar chart data: top referrers by paid conversions
  const chartData = referrers.slice(0, 8).map((r) => ({
    name:  (r.display_name || r.email || '').split('@')[0].slice(0, 12),
    paid:  r.paid_count,
  }));

  return (
    <div className="space-y-4" data-testid="referral-admin-panel">
      <PanelHeader
        title="Referral Program"
        subtitle="Invite-a-friend funnel. Inviter earns 500K tokens; invitee earns 250K on first paid checkout."
        onRefresh={load}
      />

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-[13px] py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading referral stats…
        </div>
      ) : !data ? (
        <div className="text-[13px] text-muted-foreground text-center py-8">
          Unable to load referral stats.
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <StatCard icon={Users}     label="Total redemptions"  value={data.total_redemptions}      />
            <StatCard icon={TrendingUp} label="Paid conversions"  value={data.total_paid}             color="text-emerald-600" />
            <StatCard icon={Gift}       label="Pending"           value={data.total_pending}           />
            <StatCard icon={Award}      label="Conversion rate"   value={`${data.conversion_rate_pct}%`} color="text-amber-600"
              sub={`${data.bonus_inviter_tokens?.toLocaleString()} tokens to inviter`}
            />
          </div>

          {/* Top referrers bar chart */}
          {chartData.length > 0 && (
            <div className="rounded-md border border-border bg-card p-4 shadow-sm">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-3 flex items-center gap-1.5">
                <Award className="h-3.5 w-3.5 text-primary" /> Top Referrers (paid conversions)
              </p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData} barCategoryGap="35%">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6 }}
                    formatter={(v) => [`${v} paid`, 'Conversions']}
                  />
                  <Bar dataKey="paid" fill="var(--primary)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top referrers table */}
          {referrers.length > 0 && (
            <div className="rounded-md border border-border overflow-hidden">
              <table className="w-full text-[12px]">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10.5px] font-semibold text-muted-foreground uppercase tracking-wide">User</th>
                    <th className="text-left px-3 py-2 text-[10.5px] font-semibold text-muted-foreground uppercase tracking-wide">Code</th>
                    <th className="text-right px-3 py-2 text-[10.5px] font-semibold text-muted-foreground uppercase tracking-wide">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {referrers.map((r, i) => (
                    <tr key={r.user_id} className="border-t border-border hover:bg-muted/20">
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.display_name || r.email}</div>
                        {r.display_name && <div className="text-[10.5px] text-muted-foreground">{r.email}</div>}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-primary">{r.code}</td>
                      <td className="px-3 py-2 text-right">
                        <Badge variant={i === 0 ? 'default' : 'secondary'} className="text-[10.5px]">
                          {r.paid_count}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent activity */}
          {recent.length > 0 && (
            <div className="rounded-md border border-border bg-card p-4 shadow-sm">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-3">
                Recent Redemptions (last 20)
              </p>
              <div className="space-y-1.5">
                {recent.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-[12px]">
                    <Badge
                      variant={r.status === 'paid' ? 'default' : 'secondary'}
                      className="text-[10px] w-14 justify-center flex-shrink-0"
                    >
                      {r.status}
                    </Badge>
                    <span className="text-muted-foreground font-mono text-[11px]">{r.code}</span>
                    <span className="text-muted-foreground ml-auto text-[10.5px]">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
